import React from 'react';

import { VARIANT_CLASSES } from './Button';
import { Spinner } from './Spinner';

import type { IconButtonProps, IconButtonSize } from './Button.types';

/**
 * The shape every icon button shares, whether it acts (IconButton) or
 * navigates (IconLinkButton): a round box, the focus ring, and the 44px touch
 * floor described below.
 */
export const BASE_CLASSES =
  'icon-button inline-flex items-center justify-center rounded-full transition-colors duration-normal focus:outline-none focus-visible:ring-state-focus pointer-coarse:min-h-11 pointer-coarse:min-w-11';

/**
 * Size classes for icon buttons (square dimensions)
 */
export const SIZE_CLASSES: Record<IconButtonSize, string> = {
  xs: 'w-6 h-6',
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
};

/**
 * Icon size classes within the button
 */
export const ICON_SIZE_CLASSES: Record<IconButtonSize, string> = {
  xs: 'w-4 h-4',
  sm: 'w-5 h-5',
  md: 'w-6 h-6',
  lg: 'w-7 h-7',
};

/**
 * Spinner size classes
 */
const SPINNER_SIZE_CLASSES: Record<IconButtonSize, string> = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

/**
 * IconButton component - square button with icon only
 *
 * Features:
 * - Same variants as Button (primary, secondary, contrast, outline, ghost, danger)
 * - 4 square sizes (xs: 24px, sm: 32px, md: 40px, lg: 48px)
 * - Loading state with spinner
 * - Required aria-label for accessibility
 *
 * Touch-target floor: the `size` prop controls the *visual* box, which on a
 * fine pointer (mouse) renders exactly as sized — small sizes are legitimate in
 * dense desktop toolbars. On a coarse pointer (touch) the interactive box is
 * floored to 44px (`pointer-coarse:min-h-11/min-w-11`), the documented minimum
 * touch target (design-tokens.json; WCAG 2.5.5). The glyph keeps its size and
 * simply centres in the larger tap area, so no call site can ship a sub-44px
 * touch target — the smaller `size` values stay safe to use anywhere.
 *
 * @example
 * ```tsx
 * // Primary icon button
 * <IconButton
 *   variant="primary"
 *   icon={<PlusIcon />}
 *   aria-label="Add item"
 *   onClick={handleAdd}
 * />
 *
 * // Ghost icon button (common for toolbars)
 * <IconButton
 *   variant="ghost"
 *   icon={<MenuIcon />}
 *   aria-label="Open menu"
 * />
 *
 * // Loading state
 * <IconButton
 *   variant="contrast"
 *   icon={<SaveIcon />}
 *   aria-label="Save"
 *   loading
 * />
 * ```
 */
export function IconButton({
  icon,
  'aria-label': ariaLabel,
  variant = 'ghost',
  size = 'md',
  loading = false,
  disabled,
  className = '',
  ...props
}: IconButtonProps): React.JSX.Element {
  const isDisabled = disabled || loading;

  const variantClasses = VARIANT_CLASSES[variant];
  const sizeClasses = SIZE_CLASSES[size];
  const iconSizeClasses = ICON_SIZE_CLASSES[size];
  const spinnerSizeClasses = SPINNER_SIZE_CLASSES[size];

  return (
    <button
      type="button"
      disabled={isDisabled}
      aria-label={ariaLabel}
      className={`
        ${BASE_CLASSES}
        disabled:opacity-40 disabled:cursor-not-allowed
        ${variantClasses}
        ${sizeClasses}
        ${className}
      `
        .replace(/\s+/g, ' ')
        .trim()}
      {...props}
    >
      {loading ? (
        <Spinner className={spinnerSizeClasses} />
      ) : (
        <span
          className={`shrink-0 flex items-center justify-center ${iconSizeClasses}`}
        >
          {icon}
        </span>
      )}
    </button>
  );
}

export default IconButton;
